import React from "react";
import { useField, useFormikContext } from "formik";
import FormGroup from "../formGroup";
import CheckBox from "shared/form/core/checkbox";
import { FormFieldConfig } from "../types";
import Size from "shared/enums/Size";
import { FieldResetWhenRemoved, getValueForReadOnlyFieldForStaticForm } from "shared/form/util";
import { DEFAULT_INITIAL_VALUE_CHECKBOX } from "shared/utils/constants";
import FormOverwriteField from "../overwriteField";
import { isEmpty } from "../../../utils";
import ErrorField from "../errorField";
import { useFormContextData } from "shared/form/context";
import FieldRenderType from "shared/form/renderer/enums/FieldRenderType";
import { useCopies } from "shared/hooks/useCopies";
import FormLabel from "../formLabel";

function FormCheckboxField({
  fullWidth = true,
  size = Size.LARGE,
  fixedInitialValueOnReset,
  onChange,
  changeHandler: customChangeHandler,
  ...props
}: FormFieldConfig) {
  const copies = useCopies();
  const formContextData = useFormContextData();
  const { setFieldTouched, handleChange } = useFormikContext();
  const [{ ...field }, meta] = useField({
    name: props.name,
    validate: props.validate
  });

  const changeHandler = React.useCallback(
    async e => {
      if (customChangeHandler) {
        customChangeHandler(e);
      } else {
        await handleChange(e);
        if (!meta.touched) {
          await setFieldTouched(props.name, true);
        }
        onChange?.(e);
      }
    },
    [customChangeHandler, handleChange, meta.touched, onChange, props.name, setFieldTouched]
  );

  const getConflictDisplayValue = React.useCallback(
    ({ value }) => {
      return value ? copies.get("yes") : copies.get("no");
    },
    [copies]
  );

  return (
    <React.Fragment>
      <FormGroup className={props.className} fullWidth={fullWidth} indented={props.indented}>
        {formContextData.isReadOnly ? (
          <ReadOnlyCheckbox
            label={props.label}
            additionalLabel={props.additionalLabel}
            infoLabel={props.infoLabel}
            value={field.value}
          />
        ) : (
          <CheckBox
            name={props.name}
            label={props.label}
            onBlur={field.onBlur}
            disabled={props.disabled}
            autoComplete={props.autoComplete}
            autoFocus={props.autoFocus}
            placeholder={props.placeholder}
            size={size}
            checked={field.value}
            onChange={changeHandler}
            tooltipContent={props.infoLabel}
          />
        )}
        <ErrorField meta={meta} />
        {!isEmpty(props.conflictValue) && (
          <FormOverwriteField
            forFieldName={props.name}
            disabled={props.disabled}
            customDisplayValue={getConflictDisplayValue}
          />
        )}
      </FormGroup>
      {props.enableFieldReset && (
        <FieldResetWhenRemoved
          name={props.name}
          defaultInitialValue={DEFAULT_INITIAL_VALUE_CHECKBOX}
          fixedInitialValueOnReset={fixedInitialValueOnReset}
        />
      )}
    </React.Fragment>
  );
}

export function ReadOnlyCheckbox(props: {
  label: React.ReactNode;
  additionalLabel?: React.ReactNode;
  infoLabel?: React.ReactNode;
  value: boolean;
}) {
  const copies = useCopies();

  return (
    <React.Fragment>
      <FormLabel label={props.label} infoLabel={props.infoLabel} />
      {getValueForReadOnlyFieldForStaticForm({
        renderType: FieldRenderType.CHECK_BOX,
        value: props.value ? copies.get("yes") : copies.get("no")
      })}
    </React.Fragment>
  );
}

export default FormCheckboxField;
